import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  Fade,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageSEO from '../components/PageSEO';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon,
  Visibility as VisibilityIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
  LocalFireDepartment as FireIcon,
  Diamond as DiamondIcon,
} from '@mui/icons-material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  getServices, 
  getCategories, 
  getFeaturedServices,
  ServiceWithDetails, 
  Category, 
  ServiceFilters 
} from '../lib/api/services';
import { checkItemOwnership, getPromotionStatus } from '../lib/api/promotionBids';
import { trackSearch } from '../lib/analytics';
import PromotionModal from '../components/PromotionModal';

const FindServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceWithDetails[]>([]);
  const [featuredServices, setFeaturedServices] = useState<ServiceWithDetails[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage] = useState(1);
  const [totalServices, setTotalServices] = useState(0);
  
  // Filters state
  const [filters, setFilters] = useState<ServiceFilters>({
    search: '',
    category_id: '',
    price_min: undefined,
    price_max: undefined,
    price_type: '',
    skills: [],
    delivery_days: undefined,
    featured: false,
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    limit: 12
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [promotedServices, setPromotedServices] = useState<Set<string>>(new Set());
  const [serviceOwnership, setServiceOwnership] = useState<Set<string>>(new Set());
  const [promotionModal, setPromotionModal] = useState<{
    open: boolean;
    itemId: string;
    itemTitle: string;
  }>({ open: false, itemId: '', itemTitle: '' });

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load services when filters change
  useEffect(() => {
    loadServices();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading initial data...');
      
      const [categoriesData, featuredData] = await Promise.all([
        getCategories(),
        getFeaturedServices(6)
      ]);
      
      console.log('Categories loaded:', categoriesData);
      console.log('Featured services loaded:', featuredData);
      
      setCategories(categoriesData);
      setFeaturedServices(featuredData);
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      if (err?.message?.includes('permission denied')) {
        setError(t('findServices.errors.noDataAccess'));
        // Set fallback data for testing
        setCategories([
          { id: '1', name: 'Web Development', description: 'Web development services', parent_id: null, sort_order: 1, active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '2', name: 'Mobile Development', description: 'Mobile app development', parent_id: null, sort_order: 2, active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
          { id: '3', name: 'Design', description: 'UI/UX Design services', parent_id: null, sort_order: 3, active: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() }
        ]);
        setFeaturedServices([]);
      } else {
        setError(t('findServices.errors.dataLoadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading services with filters:', filters);
      
      const result = await getServices(filters);
      console.log('Services result:', result);
      
      // Always sort services to show promoted ones first, then apply other sorting
      let sortedServices = [...result.services];
      
      // First, separate promoted and non-promoted services
      const promotedServices = sortedServices.filter(service => service.featured);
      const nonPromotedServices = sortedServices.filter(service => !service.featured);
      
      // Apply additional sorting within each group
      if (filters.sort_by === 'price-asc') {
        promotedServices.sort((a, b) => a.price - b.price);
        nonPromotedServices.sort((a, b) => a.price - b.price);
      } else if (filters.sort_by === 'price-desc') {
        promotedServices.sort((a, b) => b.price - a.price);
        nonPromotedServices.sort((a, b) => b.price - a.price);
      } else if (filters.sort_by === 'rating_avg-desc') {
        promotedServices.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        nonPromotedServices.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
      } else if (filters.sort_by === 'rating_avg-asc') {
        promotedServices.sort((a, b) => (a.rating_avg || 0) - (b.rating_avg || 0));
        nonPromotedServices.sort((a, b) => (a.rating_avg || 0) - (b.rating_avg || 0));
      } else if (filters.sort_by === 'orders_count-desc') {
        promotedServices.sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0));
        nonPromotedServices.sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0));
      } else if (filters.sort_by === 'created_at-asc') {
        promotedServices.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        nonPromotedServices.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      } else { // created_at-desc (default)
        promotedServices.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        nonPromotedServices.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }
      
      // Combine: promoted first, then non-promoted
      sortedServices = [...promotedServices, ...nonPromotedServices];
      
      // Check ownership and promotion status for each service
      const ownershipAndPromotionChecks = await Promise.all(
        result.services.map(async (service) => {
          const [isOwner, promotionStatus] = await Promise.all([
            checkItemOwnership(service.id, 'SERVICE'),
            getPromotionStatus(service.id, 'SERVICE').catch(() => ({ isPromoted: false }))
          ]);
          return { 
            serviceId: service.id, 
            isOwner, 
            isPromoted: promotionStatus.isPromoted 
          };
        })
      );
      
      const ownedServiceIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isOwner)
          .map(check => check.serviceId)
      );
      setServiceOwnership(ownedServiceIds);
      
      const promotedServiceIds = new Set(
        ownershipAndPromotionChecks
          .filter(check => check.isPromoted)
          .map(check => check.serviceId)
      );
      setPromotedServices(promotedServiceIds);
      
      setServices(sortedServices);
      setTotalPages(result.totalPages);
      setTotalServices(result.total);
    } catch (err: any) {
      console.error('Error loading services:', err);
      if (err?.message?.includes('permission denied')) {
        setError(t('findServices.errors.noServicesAccess'));
        // Set empty services for now
        setServices([]);
        setTotalPages(1);
        setTotalServices(0);
      } else {
        setError(t('findServices.errors.servicesLoadError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ServiceFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleSearch = (searchTerm: string) => {
    // Track search if there's a search term
    if (searchTerm.trim()) {
      trackSearch(searchTerm.trim(), 'services');
    }
    handleFilterChange('search', searchTerm);
  };

  const handleCategoryChange = (categoryId: string) => {
    handleFilterChange('category_id', categoryId || '');
  };

  const handlePriceChange = (min?: number, max?: number) => {
    setFilters(prev => ({
      ...prev,
      price_min: min,
      price_max: max,
      page: 1
    }));
  };

  const handleSortChange = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy as any,
      sort_order: sortOrder,
      page: 1
    }));
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category_id: '',
      price_min: undefined,
      price_max: undefined,
      price_type: '',
      skills: [],
      delivery_days: undefined,
      featured: false,
      sort_by: 'created_at',
      sort_order: 'desc',
      page: 1,
      limit: 12
    });
  };

  const toggleFavorite = (serviceId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(serviceId)) {
        newFavorites.delete(serviceId);
      } else {
        newFavorites.add(serviceId);
      }
      return newFavorites;
    });
  };

  const handlePromotionClick = (serviceId: string, serviceTitle: string) => {
    setPromotionModal({
      open: true,
      itemId: serviceId,
      itemTitle: serviceTitle
    });
  };

  const handlePromotionSuccess = () => {
    // Reload services to get updated data
    loadServices();
  };

  const formatPrice = (price: number, priceType: string) => {
    const priceStr = price.toLocaleString();
    switch (priceType) {
      case 'HOURLY': return `€${priceStr}${t('findServices.priceFormats.hourly')}`;
      case 'DAILY': return `€${priceStr}${t('findServices.priceFormats.daily')}`;
      case 'WEEKLY': return `€${priceStr}${t('findServices.priceFormats.weekly')}`;
      case 'MONTHLY': return `€${priceStr}${t('findServices.priceFormats.monthly')}`;
      default: return `€${priceStr}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('bg-BG', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return t('findServices.timeAgo.justNow');
    if (diffInHours < 24) return t('findServices.timeAgo.hoursAgo', { hours: diffInHours });
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('findServices.timeAgo.daysAgo', { days: diffInDays });
    return formatDate(dateString);
  };

  if (loading && services.length === 0) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <PageSEO pageKey="findServices" />
      {/* Animated background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Header />
      
      <Container 
        maxWidth="xl" 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 }, 
          position: 'relative', 
          zIndex: 1,
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 }, px: { xs: 2, sm: 0 } }}>
          <Typography 
            variant="h2" 
            sx={{ 
              color: '#ffffff', 
              fontWeight: 800, 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('findServices.title')}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#a0a0a0', 
              mb: 4, 
              maxWidth: 600, 
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 1, sm: 0 }
            }}
          >
            {t('findServices.subtitle')}
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: { xs: 2, md: 4 },
            backdropFilter: 'blur(10px)',
            mb: { xs: 3, md: 4 },
            mx: { xs: 1, sm: 0 },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Stack spacing={3}>
              {/* Search Bar */}
              <TextField
                fullWidth
                placeholder={t('findServices.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#28A0F0' }} />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleSearch('')} size="small">
                        <ClearIcon sx={{ color: '#a0a0a0' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover fieldset': { borderColor: '#28A0F0' },
                    '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                  },
                  '& .MuiInputBase-input': { color: '#ffffff' },
                }}
              />

              {/* Filter Toggle */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  sx={{
                    borderColor: 'rgba(40, 160, 240, 0.5)',
                    color: '#28A0F0',
                    py: { xs: 1.5, sm: 1 },
                    fontSize: { xs: '0.9rem', sm: '0.875rem' },
                    '&:hover': {
                      borderColor: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    },
                  }}
                >
                  {t('findServices.filters')} {showFilters ? t('findServices.filtersHide') : t('findServices.filtersShow')}
                </Button>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, sm: 2 }, 
                  alignItems: 'center',
                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                  flexWrap: 'wrap'
                }}>
                  <Typography variant="body2" sx={{ color: '#a0a0a0', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {t('findServices.servicesCount', { count: totalServices })}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleClearFilters}
                    sx={{ 
                      color: '#a0a0a0',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      minWidth: 'auto',
                      px: { xs: 1, sm: 2 }
                    }}
                  >
                    {t('findServices.clearFilters')}
                  </Button>
                </Box>
              </Box>

              {/* Advanced Filters */}
              {showFilters && (
                <Fade in={showFilters}>
                  <Box sx={{ pr: { xs: 1, sm: 0 } }}>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#a0a0a0' }}>{t('findServices.category')}</InputLabel>
                        <Select
                          value={filters.category_id || ''}
                          onChange={(e) => handleCategoryChange(e.target.value)}
                          sx={{
                            color: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                          }}
                        >
                          <MenuItem value="">{t('findServices.allCategories')}</MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#a0a0a0' }}>{t('findServices.priceType')}</InputLabel>
                        <Select
                          value={filters.price_type || ''}
                          onChange={(e) => handleFilterChange('price_type', e.target.value)}
                          sx={{
                            color: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                          }}
                        >
                          <MenuItem value="">{t('findServices.allPriceTypes')}</MenuItem>
                          <MenuItem value="FIXED">{t('findServices.fixedPrice')}</MenuItem>
                          <MenuItem value="HOURLY">{t('findServices.hourly')}</MenuItem>
                          <MenuItem value="DAILY">{t('findServices.daily')}</MenuItem>
                          <MenuItem value="WEEKLY">{t('findServices.weekly')}</MenuItem>
                          <MenuItem value="MONTHLY">{t('findServices.monthly')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label={t('findServices.minPrice')}
                        type="number"
                        value={filters.price_min || ''}
                        onChange={(e) => handlePriceChange(Number(e.target.value) || undefined, filters.price_max)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label={t('findServices.maxPrice')}
                        type="number"
                        value={filters.price_max || ''}
                        onChange={(e) => handlePriceChange(filters.price_min, Number(e.target.value) || undefined)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label={t('findServices.maxDeliveryDays')}
                        type="number"
                        value={filters.delivery_days || ''}
                        onChange={(e) => handleFilterChange('delivery_days', Number(e.target.value) || undefined)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: '#ffffff',
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: '#28A0F0' },
                            '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                          },
                          '& .MuiInputLabel-root': { color: '#a0a0a0' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                        }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: '#a0a0a0' }}>{t('findServices.sortBy')}</InputLabel>
                        <Select
                          value={`${filters.sort_by}-${filters.sort_order}`}
                          onChange={(e) => {
                            const [sortBy, sortOrder] = e.target.value.split('-');
                            handleSortChange(sortBy, sortOrder as 'asc' | 'desc');
                          }}
                          sx={{
                            color: '#ffffff',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                          }}
                        >
                          <MenuItem value="created_at-desc">{t('findServices.sortOptions.newest')}</MenuItem>
                          <MenuItem value="created_at-asc">{t('findServices.sortOptions.oldest')}</MenuItem>
                          <MenuItem value="price-asc">{t('findServices.sortOptions.lowestPrice')}</MenuItem>
                          <MenuItem value="price-desc">{t('findServices.sortOptions.highestPrice')}</MenuItem>
                          <MenuItem value="rating_avg-desc">{t('findServices.sortOptions.highestRating')}</MenuItem>
                          <MenuItem value="orders_count-desc">{t('findServices.sortOptions.mostViewed')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  </Box>
                </Fade>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Featured Services */}
        {featuredServices.length > 0 && (
          <Box sx={{ mb: { xs: 4, md: 6 }, px: { xs: 1, sm: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
              <TrendingUpIcon sx={{ color: '#28A0F0', mr: 1, fontSize: { xs: '1.5rem', md: '2rem' } }} />
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#ffffff', 
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
                }}
              >
                {t('findServices.recommendedServices')}
              </Typography>
            </Box>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {featuredServices.map((service) => (
                <Grid item xs={12} sm={6} lg={4} key={service.id}>
                    <Card
                      sx={{
                        background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%)',
                        border: '1px solid rgba(40, 160, 240, 0.3)',
                        borderRadius: { xs: 2, md: 3 },
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateY(-4px)' },
                          boxShadow: { xs: '0 2px 8px rgba(40, 160, 240, 0.2)', md: '0 8px 25px rgba(40, 160, 240, 0.3)' },
                        }
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start', 
                          mb: 2,
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1, sm: 0 }
                        }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: '#ffffff', 
                              fontWeight: 600, 
                              flex: 1,
                              fontSize: { xs: '1rem', md: '1.25rem' },
                              lineHeight: { xs: 1.3, md: 1.4 }
                            }}
                          >
                            {service.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={t('findServices.recommended')}
                              size="small"
                              icon={<TrendingUpIcon sx={{ fontSize: '0.8rem' }} />}
                              sx={{
                                backgroundColor: 'rgba(16, 185, 129, 0.2)',
                                color: '#10b981',
                                border: '1px solid #10b981',
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                height: { xs: 20, sm: 24 },
                                alignSelf: { xs: 'flex-start', sm: 'center' }
                              }}
                            />
                        {promotedServices.has(service.id) && (
                          <Chip
                            label={t('common.promoted')}
                            size="small"
                            icon={<FireIcon sx={{ fontSize: '0.9rem' }} />}
                            sx={{
                              background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                              color: '#ffffff',
                              border: '2px solid #ff5722',
                              fontSize: { xs: '0.6rem', sm: '0.7rem' },
                              height: { xs: 24, sm: 28 },
                              fontWeight: 'bold',
                              px: 1,
                              boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
                              animation: 'pulse 2s infinite, glow 3s infinite',
                              '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.05)' },
                                '100%': { transform: 'scale(1)' },
                              },
                              '@keyframes glow': {
                                '0%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                '50%': { boxShadow: '0 6px 20px rgba(255, 87, 34, 0.8)' },
                                '100%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                              },
                            }}
                          />
                        )}
                          </Box>
                        </Box>
                        
                        <Typography variant="body2" sx={{ color: '#d0d0d0', mb: 2, lineHeight: 1.6, flex: 1 }}>
                          {service.description.length > 120 
                            ? `${service.description.substring(0, 120)}...` 
                            : service.description}
                        </Typography>
                      
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                                {formatPrice(service.price, service.price_type)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                {service.delivery_days} дни
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                            {getTimeAgo(service.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* All Services */}
        <Box sx={{ px: { xs: 1, sm: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
            <BusinessIcon sx={{ color: '#28A0F0', mr: 1, fontSize: { xs: '1.5rem', md: '2rem' } }} />
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#ffffff', 
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}
            >
              {t('findServices.allServices')}
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              action={
                error.includes('permission denied') ? (
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={() => window.open('https://supabase.com/dashboard', '_blank')}
                  >
                    {t('findServices.errors.openSupabaseDashboardButton')}
                  </Button>
                ) : null
              }
            >
              {error}
              {error.includes('permission denied') && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('findServices.errors.fixProblem')}
                  </Typography>
                  <Typography variant="body2" component="div">
                    {t('findServices.errors.step1')}
                  </Typography>
                  <Typography variant="body2" component="div">
                    {t('findServices.errors.step2')}
                  </Typography>
                  <Typography variant="body2" component="div">
                    {t('findServices.errors.step3')}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#28A0F0' }} />
            </Box>
          ) : services.length === 0 ? (
            <Card
              sx={{
                background: 'rgba(40, 160, 240, 0.1)',
                border: '2px dashed rgba(40, 160, 240, 0.3)',
                borderRadius: { xs: 2, md: 3 },
                p: { xs: 3, md: 4 },
                textAlign: 'center',
                mx: { xs: 1, sm: 0 }
              }}
            >
              <BusinessIcon sx={{ fontSize: { xs: 48, md: 60 }, color: '#28A0F0', mb: 2 }} />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#ffffff', 
                  mb: 1,
                  fontSize: { xs: '1.1rem', md: '1.25rem' }
                }}
              >
                {t('findServices.noServices')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#a0a0a0',
                  fontSize: { xs: '0.9rem', md: '0.875rem' },
                  px: { xs: 1, sm: 0 }
                }}
              >
                {t('findServices.noServicesDescription')}
              </Typography>
            </Card>
          ) : (
            <>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {services.map((service) => (
                  <Grid item xs={12} sm={6} lg={4} key={service.id}>
                    <Card
                      sx={{
                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: { xs: 2, md: 3 },
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: { xs: 'none', md: 'translateY(-4px)' },
                          boxShadow: { xs: '0 2px 8px rgba(0, 0, 0, 0.2)', md: '0 8px 25px rgba(0, 0, 0, 0.3)' },
                        }
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, md: 3 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start', 
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 }
                          }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                color: '#ffffff', 
                                fontWeight: 600, 
                                flex: 1,
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                lineHeight: { xs: 1.3, md: 1.4 }
                              }}
                            >
                              {service.title}
                            </Typography>
                            <Box sx={{ 
                              display: 'flex', 
                              gap: 1,
                              alignSelf: { xs: 'flex-start', sm: 'center' },
                              flexWrap: 'wrap'
                            }}>
                              {promotedServices.has(service.id) && (
                                <Chip
                                  label={t('common.promoted')}
                                  size="small"
                                  icon={<FireIcon sx={{ fontSize: '0.9rem' }} />}
                                  sx={{
                                    background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
                                    color: '#ffffff',
                                    border: '2px solid #ff5722',
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                    height: { xs: 24, sm: 28 },
                                    fontWeight: 'bold',
                                    px: 1,
                                    boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
                                    animation: 'pulse 2s infinite, glow 3s infinite',
                                    '@keyframes pulse': {
                                      '0%': { transform: 'scale(1)' },
                                      '50%': { transform: 'scale(1.05)' },
                                      '100%': { transform: 'scale(1)' },
                                    },
                                    '@keyframes glow': {
                                      '0%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                      '50%': { boxShadow: '0 6px 20px rgba(255, 87, 34, 0.8)' },
                                      '100%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                                    },
                                  }}
                                />
                              )}
                              <Tooltip title={t('findServices.addToFavorites')}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleFavorite(service.id)}
                                  sx={{ color: favorites.has(service.id) ? '#ef4444' : '#a0a0a0' }}
                                >
                                  {favorites.has(service.id) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('findServices.views')}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <VisibilityIcon sx={{ color: '#a0a0a0', fontSize: '1rem' }} />
                                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                    {service.views_count}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            </Box>
                          </Box>
                          
                          <Typography variant="body2" sx={{ color: '#d0d0d0', mb: 2, lineHeight: 1.6, flex: 1 }}>
                            {service.description.length > 120 
                              ? `${service.description.substring(0, 120)}...` 
                              : service.description}
                          </Typography>
                        
                        {service.category && (
                          <Chip
                            label={service.category.name}
                            size="small"
                            sx={{
                              backgroundColor: 'rgba(40, 160, 240, 0.15)',
                              color: '#28A0F0',
                              border: '1px solid rgba(40, 160, 240, 0.3)',
                              fontSize: '0.7rem',
                              mb: 2,
                            }}
                          />
                        )}
                        
                        {service.skills.length > 0 && (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {service.skills.slice(0, 3).map((skill, index) => (
                              <Chip
                                key={index}
                                label={skill}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  color: '#ffffff',
                                  fontSize: '0.7rem',
                                }}
                              />
                            ))}
                            {service.skills.length > 3 && (
                              <Chip
                                label={`+${service.skills.length - 3}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  color: '#a0a0a0',
                                  fontSize: '0.7rem',
                                }}
                              />
                            )}
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <MoneyIcon sx={{ color: '#28A0F0', fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                                {formatPrice(service.price, service.price_type)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon sx={{ color: '#a0a0a0', fontSize: '1.2rem' }} />
                              <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                                {service.delivery_days} {t('findServices.days')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                            {getTimeAgo(service.created_at)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          gap: { xs: 1, sm: 1 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          mt: 'auto'
                        }}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => navigate(`/service/${service.id}`)}
                            sx={{
                              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                              color: '#ffffff',
                              py: { xs: 1.5, sm: 1 },
                              fontSize: { xs: '0.9rem', sm: '0.875rem' },
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                              },
                            }}
                          >
                            {t('findServices.orderNow')}
                          </Button>
                          {serviceOwnership.has(service.id) && (
                            <Tooltip title={promotedServices.has(service.id) ? t('common.managePromotion') : t('common.promoteService')}>
                              <IconButton
                                onClick={() => handlePromotionClick(service.id, service.title)}
                                sx={{
                                  color: promotedServices.has(service.id) ? '#ff5722' : '#a0a0a0',
                                  border: promotedServices.has(service.id) ? '1px solid #ff5722' : '1px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: 1,
                                  px: 1,
                                  py: 0.5,
                                  '&:hover': {
                                    backgroundColor: promotedServices.has(service.id) ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                                  }
                                }}
                              >
                                {promotedServices.has(service.id) ? <FireIcon sx={{ fontSize: '1rem' }} /> : <DiamondIcon sx={{ fontSize: '1rem' }} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: { xs: 3, md: 4 },
                  px: { xs: 1, sm: 0 }
                }}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#ffffff',
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        minWidth: { xs: 32, sm: 40 },
                        height: { xs: 32, sm: 40 },
                        '&.Mui-selected': {
                          backgroundColor: '#28A0F0',
                          color: '#ffffff',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(40, 160, 240, 0.1)',
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>

      <Footer />

      {/* Promotion Modal */}
      <PromotionModal
        open={promotionModal.open}
        onClose={() => setPromotionModal({ open: false, itemId: '', itemTitle: '' })}
        itemId={promotionModal.itemId}
        itemType="SERVICE"
        itemTitle={promotionModal.itemTitle}
        onPromotionSuccess={handlePromotionSuccess}
      />
    </Box>
  );
};

export default FindServicesPage;
